import { db } from '../firebase';
import { collection, query, where, getDocs, addDoc, doc, updateDoc, increment, getDoc } from 'firebase/firestore';
import { getUserProfileByUid, getUserProfileByEmail } from '../users/userManagement';
import { sendEventDetailEmail, sendHostRSVPNotification } from '../emails/emailService';
import { getRSVPs } from '../rsvps/rsvpService';

export const getEventByUrl = async (url) => {
  try {
    const eventsRef = collection(db, 'events');
    const q = query(eventsRef, where('url', '==', url));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const eventDoc = querySnapshot.docs[0];
      const eventData = eventDoc.data();
      
      return {
        id: eventDoc.id,
        ...eventData,
        // We don't need to fetch hostName and hostEmail here if they're already in the database
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching event:', error);
    throw error;
  }
};

export const handleRSVPAfterSignIn = async (email, eventId, showNotification, rsvpStatus = 'yes', guestCount = 0) => {
  try {
    const eventRef = doc(db, 'events', eventId);
    const eventDoc = await getDoc(eventRef);

    if (!eventDoc.exists()) {
      console.error('Event not found:', eventId);
      if (showNotification) {
        showNotification('Event not found.', 'error');
      }
      return;
    }

    const rsvpsRef = collection(db, 'rsvps');
    const q = query(rsvpsRef, where('eventId', '==', eventId), where('email', '==', email));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      if (showNotification) {
        showNotification("You're already RSVP'd to this event. You're all set!", 'success');
      }
      return;
    }

    const eventData = eventDoc.data();
    const userProfile = await getUserProfileByEmail(email);
    const rsvpName = userProfile?.name || 'Guest';
    const status = rsvpStatus === 'yes' || rsvpStatus === 'maybe' || rsvpStatus === 'no' ? rsvpStatus : 'yes';

    await addDoc(rsvpsRef, {
      email,
      eventId,
      userId: userProfile?.uid || null,
      name: rsvpName,
      status,
      guestCount: guestCount || 0,
      comment: '',
      createdAt: new Date(),
    });

    await updateDoc(eventRef, { rsvpCount: increment(1) });

    const eventPayload = { ...eventData, id: eventId, url: eventData.url };

    sendEventDetailEmail(email, rsvpName || 'Friend', eventPayload, true, false, status).catch(err => {
      console.error('Error sending RSVP confirmation email:', err);
    });

    if (eventData.hostEmail && eventData.hostEmail !== email) {
      getRSVPs(eventId).then(allRsvps => {
        sendHostRSVPNotification(
          eventData.hostEmail,
          eventData.hostName,
          eventPayload,
          { name: rsvpName, email, status, guestCount: guestCount || 0, comment: '' },
          allRsvps
        ).catch(err => console.error('Error sending host notification:', err));
      }).catch(err => console.error('Error fetching RSVPs for notification:', err));
    }

    // No success toast; event page shows confirmation modal + confetti
  } catch (error) {
    console.error('Error handling RSVP after sign-in:', error);
    if (showNotification) {
      showNotification('Error processing RSVP: ' + error.message, 'error');
    }
  }
};

export const addRSVP = async (email, eventId) => {
  const rsvpRef = collection(db, 'rsvps');
  await addDoc(rsvpRef, { 
    email, 
    eventId, 
    date: new Date().toISOString() 
  });

  // Update the RSVP count in the event document
  const eventRef = doc(db, 'events', eventId);
  await updateDoc(eventRef, {
    rsvpCount: increment(1)
  });
};

export const createEvent = async (eventData, userId) => {
  try {
    const userProfile = await getUserProfileByUid(userId);
    const newEventRef = await addDoc(collection(db, 'events'), {
      ...eventData,
      isPrivate: eventData.isPrivate || false, // Add this line
      createdBy: userId,
      hostName: userProfile?.name || 'Anonymous',
      hostEmail: userProfile?.email || 'No email provided',
      createdAt: new Date()
    });
    return newEventRef.id;
  } catch (error) {
    console.error('Error creating event:', error);
    throw error;
  }
};

export const updateEvent = async (eventId, eventData) => {
  try {
    const eventRef = doc(db, 'events', eventId);
    await updateDoc(eventRef, {
      ...eventData,
      isPrivate: eventData.isPrivate || false, // Add this line
    });
    return true;
  } catch (error) {
    console.error('Error updating event:', error);
    throw error;
  }
};
